import { logger } from '@automata/utils';
import {
  LabwareSource,
  StoredLabware,
  Workflow,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { Edge } from 'reactflow';

import {
  getLabwareSources,
  getSourceHandleForTask,
  getSourceTaskIds,
  shouldCreateTaskToTaskConnection,
} from './utils';
import { GraphEdges } from '../../../../types';
import uuid from '../../../../uuid';
import { TaskType } from '../../types';

// Helper types for better organization
type EdgeContext = {
  labwareSourceKey: string;
  labwareSource: LabwareSource | StoredLabware;
  labwareId: string;
  handleIndex: number;
  targetHandleId: string | undefined;
  targetTask: TaskType;
  taskAction: string;
};

// Create context object with all the edge-related data
const createEdgeContext = (
  labwareSourceKey: string,
  labwareSource: LabwareSource | StoredLabware,
  labwareSources: Record<string, LabwareSource | StoredLabware>,
  targetTask: TaskType,
  handlePairIdOrderByNode: Record<string, string[]>
): EdgeContext => {
  const labwareId = labwareSource.labware_id;

  // Create a deterministic mapping between labware source keys and handle indices
  const sortedLabwareSourceKeys = Object.keys(labwareSources).sort((a, b) =>
    a.localeCompare(b)
  );
  const handleIndex = sortedLabwareSourceKeys.indexOf(labwareSourceKey);

  // Use the handleIndex to assign each labware source to its own handle
  const availableHandles = handlePairIdOrderByNode[targetTask.id] || [];
  const targetHandleId = availableHandles[handleIndex] || availableHandles[0];

  const taskAction = 'action' in targetTask ? targetTask.action : 'unknown';

  return {
    labwareSourceKey,
    labwareSource,
    labwareId,
    handleIndex,
    targetHandleId,
    targetTask,
    taskAction,
  };
};

// Create task-to-task edges
const createTaskToTaskEdges = (
  edgeContext: EdgeContext,
  sourceTaskIds: string[] | null,
  flatTasksById: Record<string, TaskType>,
  handlePairIdOrderByNode: Record<string, string[]>,
  parentTask?: TaskType
): Edge[] => {
  if (!sourceTaskIds || sourceTaskIds.length === 0) {
    logger.warn(
      `Source task ID not found for labware source in task ID "${edgeContext.targetTask.id}"`
    );
    return [];
  }

  const edges: Edge[] = [];

  for (const sourceTaskId of sourceTaskIds) {
    const sourceTask = flatTasksById[sourceTaskId] || parentTask;
    const sourceTaskAction =
      sourceTask && 'action' in sourceTask && sourceTask.action;

    // Skip creating edge if source task has no action and is not a conditional parent
    if (!sourceTaskAction && !parentTask) {
      continue;
    }

    const { baseSourceHandleId, sourceHandleFormat } = getSourceHandleForTask(
      sourceTask,
      edgeContext.labwareId,
      handlePairIdOrderByNode,
      edgeContext.targetTask,
      parentTask
    );

    // Validate handles exist
    if (!baseSourceHandleId) {
      logger.warn(
        `No source handle found for task ${
          sourceTask?.id || sourceTaskId
        }. Available handles:`,
        handlePairIdOrderByNode[sourceTask?.id || sourceTaskId]
      );
      continue;
    }

    const edgeId = `labwareDependencyEdge___from-task___${sourceTaskAction}___${
      sourceTask?.id || sourceTaskId
    }-to-task___${edgeContext.taskAction}___${
      edgeContext.targetTask.id
    }___${uuid()}`;

    edges.push({
      id: edgeId,
      source: sourceTask?.id || sourceTaskId,
      target: edgeContext.targetTask.id,
      sourceHandle: sourceHandleFormat,
      targetHandle: `target.${edgeContext.targetHandleId}`,
    });
  }

  return edges;
};

// Create labware-to-task edge
const createLabwareToTaskEdge = (
  edgeContext: EdgeContext,
  workflow: Workflow
): Edge | null => {
  if (!edgeContext.targetHandleId) {
    logger.warn(
      `No target handle found for task ${edgeContext.targetTask.id} in labware-to-task connection`
    );
    return null;
  }

  const labware = workflow.labware.find((l) => l.id === edgeContext.labwareId);
  const labwareType = labware?.type || 'microplate';

  return {
    id: `labwareDependencyEdge___from-labware___${labwareType}___${
      edgeContext.labwareId
    }-to-task___${edgeContext.taskAction}___${
      edgeContext.targetTask.id
    }___${uuid()}`,
    source: edgeContext.labwareId,
    target: edgeContext.targetTask.id,
    sourceHandle: `source.${edgeContext.labwareId}`,
    targetHandle: `target.${edgeContext.targetHandleId}`,
  };
};

const processTask = (
  workflow: Workflow,
  task: TaskType,
  graphEdgesAcc: GraphEdges,
  flatTasksById: Record<string, TaskType>,
  handlePairIdOrderByNode: Record<string, string[]>,
  parentTask?: TaskType
): void => {
  const isThisContainerTask = 'tasks' in task;

  if (isThisContainerTask) {
    const nestedTasks = task.tasks;
    const conditionalTasks = 'next' in task && task.next?.tasks;
    const tasksToProcess = { ...nestedTasks, ...conditionalTasks };

    Object.values(tasksToProcess).forEach((nestedTask) => {
      processTask(
        workflow,
        nestedTask,
        graphEdgesAcc,
        flatTasksById,
        handlePairIdOrderByNode,
        task
      );
    });
  }

  const labwareSources = getLabwareSources(task);
  if (!labwareSources) return;

  // Create edges for each labware source in the task
  const taskEdges = Object.entries(labwareSources).reduce<Edge[]>(
    (edges, [labwareSourceKey, labwareSource]) => {
      const edgeContext = createEdgeContext(
        labwareSourceKey,
        labwareSource,
        labwareSources,
        task,
        handlePairIdOrderByNode
      );

      if (!edgeContext.targetHandleId) {
        logger.warn(`No target handle found for task ${task.id}`);
        return edges;
      }

      const sourceTaskIds = getSourceTaskIds(labwareSource, parentTask);
      const isTaskToTaskConnection = shouldCreateTaskToTaskConnection(
        labwareSource,
        parentTask
      );

      if (isTaskToTaskConnection) {
        const taskToTaskEdges = createTaskToTaskEdges(
          edgeContext,
          sourceTaskIds,
          flatTasksById,
          handlePairIdOrderByNode,
          parentTask
        );
        edges.push(...taskToTaskEdges);
      } else {
        const labwareToTaskEdge = createLabwareToTaskEdge(
          edgeContext,
          workflow
        );
        if (labwareToTaskEdge) {
          edges.push(labwareToTaskEdge);
        }
      }

      return edges;
    },
    []
  );

  graphEdgesAcc.push(...taskEdges);
};

const getLabwareDependencyEdges = (
  workflow: Workflow,
  flatTasksById: Record<string, TaskType>,
  handlePairIdOrderByNode: Record<string, string[]>,
  parentTask?: TaskType
): GraphEdges => {
  const graphEdgesAcc: GraphEdges = [];

  Object.values(workflow.tasks).forEach((task) =>
    processTask(
      workflow,
      task,
      graphEdgesAcc,
      flatTasksById,
      handlePairIdOrderByNode,
      parentTask
    )
  );

  return graphEdgesAcc;
};

export default getLabwareDependencyEdges;
